import req from '@sportninja/common/api/request'
import { useCallback, useState } from 'react'

/**
 * Represents a role assigned to a user within an organization
 */
export interface Role {
  role_id: number
  role_name: string
  is_owner: boolean
}

/**
 * Represents an organization where the user has a role
 */
export interface UserRole {
  id: string
  name: string
  name_full: string
  type: string
  image: any
  roles: Role[]
  is_favorite: boolean
}

/**
 * Represents pagination metadata
 */
export interface PaginationMeta {
  pagination: {
    total: number
    count: number
    per_page: number
    current_page: number
    total_pages: number
  }
}

/**
 * Represents the response from the user roles API
 */
export interface UserRolesResponse {
  data: UserRole[]
  meta: PaginationMeta
}

/**
 * Custom hook to fetch and manage user roles across organizations
 * @param page - The page number to fetch (defaults to 1)
 * @param perPage - Number of items per page (defaults to 50)
 * @returns Object containing user roles data, loading state, error state, pagination info, and refetch function
 */
export const useUsersMeUserRoles = (page = 1, perPage = 50) => {
  const [data, setData] = useState<UserRole[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [pagination, setPagination] = useState<
    PaginationMeta['pagination'] | null
  >(null)

  /**
   * Fetches user roles data from the API
   */
  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const response = await req(
        `/users/me/user-roles?page=${page}&per_page=${perPage}`
      )

      setData(response.data)
      setPagination(response.meta.pagination)
    } catch (err) {
      setError(
        err instanceof Error
          ? err
          : new Error('An error occurred while fetching user roles')
      )
    } finally {
      setLoading(false)
    }
  }, [page, perPage])

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchData,
  }
}
